import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Textarea,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import {
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { getAgencies, client } from '../../services/api';
import type { AgencyResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { useShallow } from 'zustand/react/shallow';

type EvvVisitSearchResult = {
  visit_id: string;
  patient_name: string;
  mrn?: string | null;
  visit_date?: string | null;
  visit_type?: string | null;
  status?: string | null;
};

type EvvCapturePreviewItem = {
  source_s3_key: string;
  captured_at: string;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  is_latest: boolean;
  emr_signature_status: string;
  emr_s3_status?: string | null;
  signature_id?: string | null;
  already_retired: boolean;
};

type EvvPreview = {
  agency_id: string;
  rogerdocs_agency_id: string;
  visit_id: string;
  rogerdocs_visit_id: string;
  patient_name?: string | null;
  visit_date?: string | null;
  captures: EvvCapturePreviewItem[];
};

type EvvDeleteResult = {
  request_id: string;
  status: string;
  docs_mongo_status?: string | null;
  docs_s3_status?: string | null;
  emr_signature_status?: string | null;
  emr_s3_status?: string | null;
  error?: string | null;
};

function formatAgencyLabel(agency: AgencyResponse): string {
  const systemName = agency.system_name.trim();
  const writtenName = (agency.written_name ?? '').trim();
  if (!writtenName || writtenName.toLowerCase() === systemName.toLowerCase()) {
    return systemName;
  }
  return `${writtenName} (${systemName})`;
}

const EvvCaptureCorrectionPanel: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [agencies, setAgencies] = useState<AgencyResponse[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [agencyId, setAgencyId] = useState('');
  const [visitSearch, setVisitSearch] = useState('');
  const [visits, setVisits] = useState<EvvVisitSearchResult[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [selectedVisit, setSelectedVisit] =
    useState<EvvVisitSearchResult | null>(null);
  const [preview, setPreview] = useState<EvvPreview | null>(null);
  const [selectedCapture, setSelectedCapture] =
    useState<EvvCapturePreviewItem | null>(null);
  const [result, setResult] = useState<EvvDeleteResult | null>(null);
  const [reason, setReason] = useState('');
  const [supportReference, setSupportReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const visitSearchToken = useRef(0);

  const agencyOptions = useMemo(
    () =>
      [...agencies]
        .sort((a, b) =>
          formatAgencyLabel(a).localeCompare(formatAgencyLabel(b))
        )
        .map(agency => ({
          value: agency.id,
          label: formatAgencyLabel(agency),
        })),
    [agencies]
  );
  const selectedAgency = agencies.find(agency => agency.id === agencyId);
  const canSearchVisits = Boolean(agencyId && visitSearch.trim().length >= 2);
  const canPreview = Boolean(selectedVisit);
  const canDelete = Boolean(
    selectedCapture && reason.trim().length >= 5 && confirmed
  );
  const canRetry = Boolean(result?.request_id);

  useEffect(() => {
    let active = true;
    const loadAgencies = async () => {
      try {
        const response = await getAgencies({
          query: { limit: 1000 },
          throwOnError: true,
        });
        if (active) setAgencies(response.data ?? []);
      } catch (error) {
        console.error('Failed to load agencies for EVV correction', error);
        if (active) {
          showError(
            'Agencies unavailable',
            getUserFacingApiMessage(error, 'Unable to load agencies.')
          );
        }
      } finally {
        if (active) setLoadingAgencies(false);
      }
    };
    void loadAgencies();
    return () => {
      active = false;
    };
  }, [showError]);

  useEffect(() => {
    const token = ++visitSearchToken.current;
    const timer = window.setTimeout(async () => {
      if (!canSearchVisits) {
        setVisits([]);
        setLoadingVisits(false);
        return;
      }
      setLoadingVisits(true);
      try {
        const response = await client.request<
          EvvVisitSearchResult[],
          unknown,
          true
        >({
          method: 'GET',
          url: '/internal-tools/evv-corrections/visits',
          query: { agency_id: agencyId, search: visitSearch.trim() },
          throwOnError: true,
        });
        if (token === visitSearchToken.current) {
          setVisits(
            (response.data as EvvVisitSearchResult[] | undefined) ?? []
          );
        }
      } catch (error) {
        if (token !== visitSearchToken.current) return;
        console.error('Failed to search EVV correction visits', error);
        setVisits([]);
        showError(
          'Visit search failed',
          getUserFacingApiMessage(error, 'Unable to find visits.')
        );
      } finally {
        if (token === visitSearchToken.current) setLoadingVisits(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [agencyId, canSearchVisits, showError, visitSearch]);

  const clearVisitSelection = () => {
    setSelectedVisit(null);
    setPreview(null);
    setSelectedCapture(null);
    setResult(null);
    setConfirmed(false);
  };

  const resetToolState = () => {
    setAgencyId('');
    setVisitSearch('');
    setVisits([]);
    setSelectedVisit(null);
    setPreview(null);
    setSelectedCapture(null);
    setResult(null);
    setReason('');
    setSupportReference('');
    setConfirmed(false);
  };

  const selectAgency = (value: string) => {
    setAgencyId(value);
    setVisitSearch('');
    setVisits([]);
    clearVisitSelection();
  };

  const loadPreview = async () => {
    if (!canPreview || !selectedVisit) return;
    setLoading(true);
    setResult(null);
    setSelectedCapture(null);
    setConfirmed(false);
    try {
      const response = await client.request<EvvPreview, unknown, true>({
        method: 'GET',
        url: '/internal-tools/evv-corrections/preview',
        query: {
          agency_id: agencyId,
          visit_id: selectedVisit.visit_id,
        },
        throwOnError: true,
      });
      setPreview((response.data as EvvPreview | undefined) ?? null);
    } catch (error) {
      console.error('Failed to preview EVV correction', error);
      showError('Preview failed', 'Unable to load captures for that visit.');
    } finally {
      setLoading(false);
    }
  };

  const applyResult = (deleteResult: EvvDeleteResult | null) => {
    setResult(deleteResult);
    if (!deleteResult) return;
    if (deleteResult.status === 'succeeded') {
      showSuccess(
        'Capture retired',
        'The incorrect capture was retired in RogerDocs and RogerEMR.'
      );
      resetToolState();
    } else {
      showError('Correction needs follow-up', 'Review the component statuses.');
    }
  };

  const deleteCapture = async () => {
    if (!canDelete || !selectedCapture || !selectedVisit) return;
    setLoading(true);
    try {
      const response = await client.request<EvvDeleteResult, unknown, true>({
        method: 'POST',
        url: '/internal-tools/evv-corrections/delete',
        body: {
          agency_id: agencyId,
          visit_id: selectedVisit.visit_id,
          rogerdocs_visit_id: preview?.rogerdocs_visit_id,
          source_s3_key: selectedCapture.source_s3_key,
          reason: reason.trim(),
          support_reference: supportReference.trim() || null,
        },
        throwOnError: true,
      });
      applyResult((response.data as EvvDeleteResult | undefined) ?? null);
    } catch (error) {
      console.error('Failed to retire EVV capture', error);
      showError(
        'Retirement failed',
        'Unable to retire the Patient Signature capture.'
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyCapture = async () => {
    if (!selectedCapture || !selectedVisit) return;
    setLoading(true);
    try {
      const response = await client.request<EvvDeleteResult, unknown, true>({
        method: 'POST',
        url: '/internal-tools/evv-corrections/verify',
        body: {
          agency_id: agencyId,
          visit_id: selectedVisit.visit_id,
          rogerdocs_visit_id: preview?.rogerdocs_visit_id,
          source_s3_key: selectedCapture.source_s3_key,
          request_id: result?.request_id ?? null,
        },
        throwOnError: true,
      });
      applyResult((response.data as EvvDeleteResult | undefined) ?? null);
    } catch (error) {
      console.error('Failed to verify EVV correction', error);
      showError(
        'Status check failed',
        'Unable to check the correction status.'
      );
    } finally {
      setLoading(false);
    }
  };

  const retryCapture = async () => {
    if (!result?.request_id) return;
    setLoading(true);
    try {
      const response = await client.request<EvvDeleteResult, unknown, true>({
        method: 'POST',
        url: `/internal-tools/evv-corrections/retry/${result.request_id}`,
        throwOnError: true,
      });
      applyResult((response.data as EvvDeleteResult | undefined) ?? null);
    } catch (error) {
      console.error('Failed to retry EVV correction', error);
      showError(
        'Retry failed',
        'Unable to retry the Patient Signature correction.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <p className='mb-4 text-sm text-muted-foreground'>
        Find the visit, review its signature captures, and retire only the
        incorrect one.
      </p>
      <div className='space-y-6'>
        <section className='space-y-3'>
          <div>
            <h3 className='font-medium'>1. Find the visit</h3>
            <p className='text-sm text-muted-foreground'>
              Choose an agency, then search by patient name, MRN, or visit ID.
            </p>
          </div>
          <div className='w-full space-y-2 md:max-w-md'>
            <VirtualizedFormSelect
              key={`evv-agency-${agencyId || 'none'}-${agencies.length}`}
              label='Agency'
              placeholder={
                loadingAgencies ? 'Loading agencies…' : 'Search agencies…'
              }
              options={agencyOptions}
              sortOptions
              selectSize='sm'
              isClearable={false}
              defaultValue={agencyId}
              onChange={event => selectAgency(event.target.value)}
              menuPortalTarget={null}
              menuPosition='absolute'
              disabled={loadingAgencies || loading}
            />
          </div>
          {selectedAgency && (
            <div className='space-y-2'>
              <Label htmlFor='evv-visit-search'>Patient or visit</Label>
              <Input
                id='evv-visit-search'
                value={visitSearch}
                onChange={event => {
                  setVisitSearch(event.target.value);
                  clearVisitSelection();
                }}
                placeholder='Search by patient name, MRN, or visit ID'
                inputSize='sm'
                disabled={loading}
              />
            </div>
          )}
          {loadingVisits && (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' /> Searching visits…
            </div>
          )}
          {!loadingVisits && canSearchVisits && visits.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              No visits found. Try a different name, MRN, or visit ID.
            </p>
          )}
          {visits.length > 0 && !selectedVisit && (
            <div className='space-y-2' aria-label='Matching visits'>
              {visits.map(visit => (
                <button
                  key={visit.visit_id}
                  type='button'
                  className='w-full rounded-md border p-3 text-left transition hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  onClick={() => {
                    setSelectedVisit(visit);
                    setPreview(null);
                    setSelectedCapture(null);
                    setResult(null);
                  }}
                >
                  <div className='font-medium'>{visit.patient_name}</div>
                  <div className='text-sm text-muted-foreground'>
                    {visit.visit_date || 'Date unavailable'}
                    {visit.mrn ? ` · MRN ${visit.mrn}` : ''}
                    {visit.visit_type ? ` · ${visit.visit_type}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {selectedVisit && (
          <section className='space-y-3 rounded-md border p-4'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <h3 className='font-medium'>2. Confirm the source visit</h3>
                <p className='text-sm text-muted-foreground'>
                  {selectedVisit.patient_name} ·{' '}
                  {selectedVisit.visit_date || 'Date unavailable'}
                  {selectedVisit.mrn ? ` · MRN ${selectedVisit.mrn}` : ''}
                </p>
              </div>
              <Button
                variant='ghost'
                size='sm'
                onClick={clearVisitSelection}
                disabled={loading}
                aria-label='Choose a different visit'
              >
                <X className='mr-1 h-4 w-4' /> Change
              </Button>
            </div>
            <p className='text-sm text-muted-foreground'>
              The matching RogerDocs source record will be found automatically.
            </p>
            <Button onClick={loadPreview} disabled={!canPreview || loading}>
              {loading ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Search className='mr-2 h-4 w-4' />
              )}
              Review captures
            </Button>
          </section>
        )}

        {preview && (
          <section className='space-y-3'>
            <div>
              <h3 className='font-medium'>3. Choose the incorrect capture</h3>
              <p className='text-sm text-muted-foreground'>
                Patient:{' '}
                {preview.patient_name ||
                  selectedVisit?.patient_name ||
                  'Unknown'}
                {' · '}Visit date: {preview.visit_date || 'Unknown'}
              </p>
            </div>
            {preview.captures.length === 0 && (
              <div className='rounded-md border p-4 text-sm'>
                No active captures found for this visit.
              </div>
            )}
            {preview.captures.map(capture => (
              <label
                key={capture.source_s3_key}
                className='block cursor-pointer rounded-md border p-4 transition has-[:checked]:border-primary has-[:checked]:bg-muted/30'
              >
                <div className='flex items-start gap-3'>
                  <input
                    className='mt-1'
                    type='radio'
                    name='evv-capture-selection'
                    checked={
                      selectedCapture?.source_s3_key === capture.source_s3_key
                    }
                    onChange={() => {
                      setSelectedCapture(capture);
                      setConfirmed(false);
                      setResult(null);
                    }}
                  />
                  <div className='min-w-0 space-y-1'>
                    <div className='font-medium'>
                      {capture.is_latest
                        ? 'Most recent capture'
                        : 'Earlier capture'}
                    </div>
                    <div className='text-sm'>
                      Captured {new Date(capture.captured_at).toLocaleString()}
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      {capture.address || 'Location not available'}
                      {capture.latitude != null && capture.longitude != null
                        ? ` · ${capture.latitude}, ${capture.longitude}`
                        : ''}
                    </div>
                    <details className='text-xs text-muted-foreground'>
                      <summary className='inline-flex cursor-pointer items-center gap-1'>
                        Technical details <ChevronDown className='h-3 w-3' />
                      </summary>
                      <div className='mt-1 break-all'>
                        Source key: {capture.source_s3_key}
                        <br />
                        RogerEMR row: {capture.emr_signature_status}
                        <br />
                        RogerEMR file: {capture.emr_s3_status || 'unknown'}
                      </div>
                    </details>
                  </div>
                </div>
              </label>
            ))}
          </section>
        )}

        {selectedCapture && (
          <section className='space-y-3 rounded-md border p-4'>
            <div>
              <h3 className='font-medium'>4. Explain and confirm</h3>
              <p className='text-sm text-muted-foreground'>
                Retiring a capture is permanent. This keeps the other captures
                intact.
              </p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='evv-reason'>
                Why should this capture be retired?
              </Label>
              <Textarea
                id='evv-reason'
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder='For example: captured for the wrong patient'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='evv-support-reference'>
                Support ticket (optional)
              </Label>
              <Input
                id='evv-support-reference'
                value={supportReference}
                onChange={event => setSupportReference(event.target.value)}
                placeholder='Ticket or incident number'
                inputSize='sm'
              />
            </div>
            <label className='flex items-start gap-2 text-sm'>
              <input
                className='mt-1'
                type='checkbox'
                checked={confirmed}
                onChange={event => setConfirmed(event.target.checked)}
              />
              I confirmed this is the exact incorrect capture to retire.
            </label>
            <Button
              variant='destructive'
              onClick={deleteCapture}
              disabled={!canDelete || loading}
            >
              {loading ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Trash2 className='mr-2 h-4 w-4' />
              )}
              Retire this capture
            </Button>
          </section>
        )}

        {result && (
          <section className='space-y-3 rounded-md border p-4 text-sm'>
            <div className='font-medium'>Result: {result.status}</div>
            <div className='grid gap-1 sm:grid-cols-2'>
              <div>RogerDocs data: {result.docs_mongo_status || 'unknown'}</div>
              <div>RogerDocs file: {result.docs_s3_status || 'unknown'}</div>
              <div>
                RogerEMR signature: {result.emr_signature_status || 'unknown'}
              </div>
              <div>RogerEMR file: {result.emr_s3_status || 'unknown'}</div>
            </div>
            {result.status !== 'succeeded' && (
              <div className='flex flex-wrap gap-2'>
                <Button
                  onClick={verifyCapture}
                  disabled={!selectedCapture || loading}
                >
                  <ShieldCheck className='mr-2 h-4 w-4' /> Check again
                </Button>
                <Button onClick={retryCapture} disabled={!canRetry || loading}>
                  <RefreshCw className='mr-2 h-4 w-4' /> Try again
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </section>
  );
};

export default EvvCaptureCorrectionPanel;
